import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {
    SchemaPropertyType,
    type SchemaProperty,
    type StorageDatabaseSchema,
} from "@vesta/vos-types/modules/platformLevel/storageDatabases/domain/StorageDatabaseSchema";
import {ottopilotEventContainersWorkspaceCapabilityDefaultColumns} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainersWorkspaceCapabilitySchema";
import {toCamelCase} from "@vesta/vos-types/generic/utilities/stringCase";
import type {OttopilotDispositionColumnConfig} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotDispositionColumnConfig";
import type {OttopilotOrganizationalUnitColumnConfig} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotOrganizationalUnitColumnConfig";
import {z as zod} from "zod";

export enum OttopilotEventContainerTemplateAdditionalColumnType {
    btlActivity = "f1cbbf54-ca49-4afd-a9fc-df30d35267b0",
    email = "7a27ffb5-ef3d-4165-aae3-e04bb8259430",
    geographyCountries = "1342c716-848c-4031-908b-5f71da616095",
    geographyIndiaCities = "ab184ba0-de6a-4824-90ae-5ecbc1f39dc0",
    geographyIndiaPincodes = "19d2fc77-1fab-4f19-9d0b-271063b24a94",
    geographyIndiaStates = "9dda6ed8-cccf-4be1-b710-cc0dde569fd0",
    geographyIndiaZones = "e3ff369e-a364-45db-9561-76c40b42a1fe",
    geographyLatitude = "f38694a4-cd96-4196-8d7e-bdb151c60ee3",
    geographyLongitude = "f4b859b8-9353-4419-9d67-33409dc72cbc",
    indianPhoneNumber = "80edaaf6-3873-42bc-a18d-cd587999a2c7",
    // Retired after the select-options-to-UDO migration: multiSelect = "69bc6f37-72c7-4b3f-a650-f8ddfb6944fe",
    number = "eb5fde67-e06c-41b2-9784-9ba5a2aff5a3",
    ou = "8b626dbc-89e9-43e2-b2db-7a59c291ad5e",
    product = "42b70ff0-1988-48ca-b672-dd7aee51adcb",
    role = "cccf7e85-412b-4434-a3b3-9e2ab612925d",
    // Retired after the select-options-to-UDO migration: select = "58cf0dda-6a9b-4995-9a76-76661e9c9de5",
    text = "a0b66d48-33e5-4f31-9657-fb5b09403d55",
    timestamp = "b5743be4-ddc9-45c0-af04-193c9624ad11",
    user = "d300311e-4ca1-47e4-9a00-fd925c5e10b2",
    userDefinedObject = "1b5d7cbe-532c-4f6a-ad18-21301ac8af01",
    userDefinedObjects = "a92d2dba-5710-430d-8978-5bd10040fca6",
    uuid = "54f2b0a7-6b6e-44dd-9163-b83c2aa354eb",
}

export type OttopilotEventContainerTemplateAdditionalColumnValueTypeMap = {
    [OttopilotEventContainerTemplateAdditionalColumnType.btlActivity]: Uuid;
    [OttopilotEventContainerTemplateAdditionalColumnType.email]: string;
    [OttopilotEventContainerTemplateAdditionalColumnType.geographyCountries]: Uuid;
    [OttopilotEventContainerTemplateAdditionalColumnType.geographyIndiaCities]: Uuid;
    [OttopilotEventContainerTemplateAdditionalColumnType.geographyIndiaPincodes]: Uuid;
    [OttopilotEventContainerTemplateAdditionalColumnType.geographyIndiaStates]: Uuid;
    [OttopilotEventContainerTemplateAdditionalColumnType.geographyIndiaZones]: Uuid;
    [OttopilotEventContainerTemplateAdditionalColumnType.geographyLatitude]: number;
    [OttopilotEventContainerTemplateAdditionalColumnType.geographyLongitude]: number;
    [OttopilotEventContainerTemplateAdditionalColumnType.indianPhoneNumber]: string;
    [OttopilotEventContainerTemplateAdditionalColumnType.number]: number;
    [OttopilotEventContainerTemplateAdditionalColumnType.ou]: Uuid;
    [OttopilotEventContainerTemplateAdditionalColumnType.product]: Uuid;
    [OttopilotEventContainerTemplateAdditionalColumnType.role]: Uuid;
    [OttopilotEventContainerTemplateAdditionalColumnType.text]: string;
    [OttopilotEventContainerTemplateAdditionalColumnType.timestamp]: Iso8601DateTime;
    [OttopilotEventContainerTemplateAdditionalColumnType.user]: Uuid;
    [OttopilotEventContainerTemplateAdditionalColumnType.userDefinedObject]: Uuid;
    [OttopilotEventContainerTemplateAdditionalColumnType.userDefinedObjects]: ReadonlyArray<Uuid>;
    [OttopilotEventContainerTemplateAdditionalColumnType.uuid]: Uuid;
};

export enum OttopilotEventContainerTemplateAdditionalColumnSourceType {
    physical = "2c97be19-6ff1-4258-a7b1-ae222a63f49e",
    resolver = "4e9d8e76-e55b-4872-9519-bb9936510644",
}

export type OttopilotEventContainerTemplateAdditionalColumnSource =
    | {
          required: boolean;
          type: OttopilotEventContainerTemplateAdditionalColumnSourceType.physical;
          unique: boolean;
      }
    | {
          sql: string;
          type: OttopilotEventContainerTemplateAdditionalColumnSourceType.resolver;
      };

type OttopilotEventContainerTemplateAdditionalColumnBase = {
    displayName: string;
    id: Uuid;
    source: OttopilotEventContainerTemplateAdditionalColumnSource;
};

type OttopilotEventContainerTemplateAdditionalColumnWithoutConfiguration =
    OttopilotEventContainerTemplateAdditionalColumnBase & {
        type:
            | OttopilotEventContainerTemplateAdditionalColumnType.btlActivity
            | OttopilotEventContainerTemplateAdditionalColumnType.email
            | OttopilotEventContainerTemplateAdditionalColumnType.geographyCountries
            | OttopilotEventContainerTemplateAdditionalColumnType.geographyIndiaCities
            | OttopilotEventContainerTemplateAdditionalColumnType.geographyIndiaPincodes
            | OttopilotEventContainerTemplateAdditionalColumnType.geographyIndiaStates
            | OttopilotEventContainerTemplateAdditionalColumnType.geographyIndiaZones
            | OttopilotEventContainerTemplateAdditionalColumnType.geographyLatitude
            | OttopilotEventContainerTemplateAdditionalColumnType.geographyLongitude
            | OttopilotEventContainerTemplateAdditionalColumnType.indianPhoneNumber
            | OttopilotEventContainerTemplateAdditionalColumnType.number
            | OttopilotEventContainerTemplateAdditionalColumnType.ou
            | OttopilotEventContainerTemplateAdditionalColumnType.product
            | OttopilotEventContainerTemplateAdditionalColumnType.role
            | OttopilotEventContainerTemplateAdditionalColumnType.text
            | OttopilotEventContainerTemplateAdditionalColumnType.timestamp
            | OttopilotEventContainerTemplateAdditionalColumnType.user
            | OttopilotEventContainerTemplateAdditionalColumnType.uuid;
    };

type OttopilotEventContainerTemplateUserDefinedObjectAdditionalColumn =
    OttopilotEventContainerTemplateAdditionalColumnBase & {
        configuration: {
            userDefinedObjectTemplateId: Uuid;
        };
        type: OttopilotEventContainerTemplateAdditionalColumnType.userDefinedObject;
    };

type OttopilotEventContainerTemplateUserDefinedObjectsAdditionalColumn =
    OttopilotEventContainerTemplateAdditionalColumnBase & {
        configuration: {
            userDefinedObjectTemplateId: Uuid;
        };
        type: OttopilotEventContainerTemplateAdditionalColumnType.userDefinedObjects;
    };

export type OttopilotEventContainerTemplateAdditionalColumn =
    | OttopilotEventContainerTemplateAdditionalColumnWithoutConfiguration
    | OttopilotEventContainerTemplateUserDefinedObjectAdditionalColumn
    | OttopilotEventContainerTemplateUserDefinedObjectsAdditionalColumn;

export type OttopilotEventContainerTemplateAdditionalColumnValue<
    TColumn extends OttopilotEventContainerTemplateAdditionalColumn,
> = TColumn extends OttopilotEventContainerTemplateAdditionalColumn
    ? OttopilotEventContainerTemplateAdditionalColumnValueTypeMap[TColumn["type"]] |
          (TColumn extends {
              source: {
                  required: true;
                  type: OttopilotEventContainerTemplateAdditionalColumnSourceType.physical;
              };
          }
              ? never
              : null)
    : never;

export const ottopilotEventContainerTemplateAdditionalColumnSourceValidator: zod.ZodType<OttopilotEventContainerTemplateAdditionalColumnSource> =
    zod.discriminatedUnion("type", [
        zod.strictObject({
            required: zod.boolean(),
            type: zod.literal(OttopilotEventContainerTemplateAdditionalColumnSourceType.physical),
            unique: zod.boolean(),
        }),
        zod.strictObject({
            sql: zod.string().refine((value) => value.trim() !== "", "source.sql cannot be blank"),
            type: zod.literal(OttopilotEventContainerTemplateAdditionalColumnSourceType.resolver),
        }),
    ]);

const eventContainerTemplateAdditionalColumnBaseValidator = zod.strictObject({
    displayName: zod.string(),
    id: uuidZodValidator,
    source: ottopilotEventContainerTemplateAdditionalColumnSourceValidator,
});

const eventContainerTemplateAdditionalColumnWithoutConfigurationValidator = eventContainerTemplateAdditionalColumnBaseValidator.extend({
    type: zod.enum([
        OttopilotEventContainerTemplateAdditionalColumnType.btlActivity,
        OttopilotEventContainerTemplateAdditionalColumnType.email,
        OttopilotEventContainerTemplateAdditionalColumnType.geographyCountries,
        OttopilotEventContainerTemplateAdditionalColumnType.geographyIndiaCities,
        OttopilotEventContainerTemplateAdditionalColumnType.geographyIndiaPincodes,
        OttopilotEventContainerTemplateAdditionalColumnType.geographyIndiaStates,
        OttopilotEventContainerTemplateAdditionalColumnType.geographyIndiaZones,
        OttopilotEventContainerTemplateAdditionalColumnType.geographyLatitude,
        OttopilotEventContainerTemplateAdditionalColumnType.geographyLongitude,
        OttopilotEventContainerTemplateAdditionalColumnType.indianPhoneNumber,
        OttopilotEventContainerTemplateAdditionalColumnType.number,
        OttopilotEventContainerTemplateAdditionalColumnType.ou,
        OttopilotEventContainerTemplateAdditionalColumnType.product,
        OttopilotEventContainerTemplateAdditionalColumnType.role,
        OttopilotEventContainerTemplateAdditionalColumnType.text,
        OttopilotEventContainerTemplateAdditionalColumnType.timestamp,
        OttopilotEventContainerTemplateAdditionalColumnType.user,
        OttopilotEventContainerTemplateAdditionalColumnType.uuid,
    ]),
});

const eventContainerTemplateUserDefinedObjectAdditionalColumnValidator = eventContainerTemplateAdditionalColumnBaseValidator.extend({
    configuration: zod.strictObject({
        userDefinedObjectTemplateId: uuidZodValidator,
    }),
    type: zod.literal(OttopilotEventContainerTemplateAdditionalColumnType.userDefinedObject),
});

const eventContainerTemplateUserDefinedObjectsAdditionalColumnValidator = eventContainerTemplateAdditionalColumnBaseValidator.extend({
    configuration: zod.strictObject({
        userDefinedObjectTemplateId: uuidZodValidator,
    }),
    type: zod.literal(OttopilotEventContainerTemplateAdditionalColumnType.userDefinedObjects),
});

export const ottopilotEventContainerTemplateAdditionalColumnValidator: zod.ZodType<OttopilotEventContainerTemplateAdditionalColumn> =
    zod.union([
        eventContainerTemplateAdditionalColumnWithoutConfigurationValidator,
        eventContainerTemplateUserDefinedObjectAdditionalColumnValidator,
        eventContainerTemplateUserDefinedObjectsAdditionalColumnValidator,
    ]);

export const ottopilotEventContainerTemplateAdditionalColumnsValidator = ottopilotEventContainerTemplateAdditionalColumnValidator
    .array()
    .superRefine((additionalColumns, context) => {
        const seenAdditionalColumnIds = new Set<Uuid>();
        for (const [index, additionalColumn] of additionalColumns.entries()) {
            if (seenAdditionalColumnIds.has(additionalColumn.id)) {
                context.addIssue({
                    code: "custom",
                    message: `Duplicate additional column id: ${additionalColumn.id}`,
                    path: [index, "id"],
                });
            }
            seenAdditionalColumnIds.add(additionalColumn.id);
        }
    });

export function getSchemaPropertyTypeFromOttopilotEventContainerTemplateAdditionalColumnType(
    additionalColumnType: OttopilotEventContainerTemplateAdditionalColumnType,
): SchemaPropertyType {
    switch (additionalColumnType) {
        case OttopilotEventContainerTemplateAdditionalColumnType.email:
        case OttopilotEventContainerTemplateAdditionalColumnType.indianPhoneNumber:
        case OttopilotEventContainerTemplateAdditionalColumnType.text: {
            return SchemaPropertyType.string;
        }
        case OttopilotEventContainerTemplateAdditionalColumnType.userDefinedObject: {
            return SchemaPropertyType.uuid;
        }
        case OttopilotEventContainerTemplateAdditionalColumnType.userDefinedObjects: {
            return SchemaPropertyType.json;
        }
        case OttopilotEventContainerTemplateAdditionalColumnType.number: {
            return SchemaPropertyType.number;
        }
        case OttopilotEventContainerTemplateAdditionalColumnType.btlActivity:
        case OttopilotEventContainerTemplateAdditionalColumnType.geographyCountries:
        case OttopilotEventContainerTemplateAdditionalColumnType.geographyIndiaCities:
        case OttopilotEventContainerTemplateAdditionalColumnType.geographyIndiaPincodes:
        case OttopilotEventContainerTemplateAdditionalColumnType.geographyIndiaStates:
        case OttopilotEventContainerTemplateAdditionalColumnType.geographyIndiaZones:
        case OttopilotEventContainerTemplateAdditionalColumnType.ou:
        case OttopilotEventContainerTemplateAdditionalColumnType.product:
        case OttopilotEventContainerTemplateAdditionalColumnType.role:
        case OttopilotEventContainerTemplateAdditionalColumnType.user:
        case OttopilotEventContainerTemplateAdditionalColumnType.uuid: {
            return SchemaPropertyType.uuid;
        }
        case OttopilotEventContainerTemplateAdditionalColumnType.geographyLatitude:
        case OttopilotEventContainerTemplateAdditionalColumnType.geographyLongitude: {
            return SchemaPropertyType.number;
        }
        case OttopilotEventContainerTemplateAdditionalColumnType.timestamp: {
            return SchemaPropertyType.timestamp;
        }
        default: {
            throw new Error(`Unsupported event container template additional column type: ${String(additionalColumnType)}`);
        }
    }
}

export function getSchemaPropertiesFromOttopilotEventContainerTemplateAdditionalColumns({
    additionalColumns,
    dispositionColumnConfig,
    organizationalUnitColumnConfig,
}: {
    additionalColumns: Array<OttopilotEventContainerTemplateAdditionalColumn>;
    dispositionColumnConfig: OttopilotDispositionColumnConfig;
    organizationalUnitColumnConfig: OttopilotOrganizationalUnitColumnConfig;
}): Record<string, SchemaProperty> {
    const dispositionColumn = additionalColumns.find((column) => column.id === dispositionColumnConfig.columnId);
    if (
        dispositionColumn === undefined
        || dispositionColumn.type !== OttopilotEventContainerTemplateAdditionalColumnType.userDefinedObject
        || dispositionColumn.source.type !== OttopilotEventContainerTemplateAdditionalColumnSourceType.physical
        || dispositionColumn.source.required !== true
    ) {
        throw new Error(
            `Disposition column ${dispositionColumnConfig.columnId} must be a required physical single-value UDO additional column`,
        );
    }
    const organizationalUnitColumn = additionalColumns.find((column) => column.id === organizationalUnitColumnConfig.columnId);
    if (
        organizationalUnitColumn === undefined
        || organizationalUnitColumn.type !== OttopilotEventContainerTemplateAdditionalColumnType.ou
        || organizationalUnitColumn.source.type !== OttopilotEventContainerTemplateAdditionalColumnSourceType.physical
        || organizationalUnitColumn.source.required !== true
    ) {
        throw new Error(
            `Organizational Unit column ${organizationalUnitColumnConfig.columnId} must be a required physical OU additional column`,
        );
    }
    const properties: Record<string, SchemaProperty> = {};
    for (const [key, value] of Object.entries(ottopilotEventContainersWorkspaceCapabilityDefaultColumns)) {
        properties[key] = {
            id: generateUuid(),
            ...value,
        };
    }

    for (const additionalColumn of additionalColumns) {
        if (additionalColumn.source.type === OttopilotEventContainerTemplateAdditionalColumnSourceType.resolver) {
            continue;
        }
        if (properties[additionalColumn.id] !== undefined) {
            throw new Error(`Duplicate additional column id: ${additionalColumn.id}`);
        }
        properties[additionalColumn.id] = {
            defaultValue: null,
            displayName: additionalColumn.displayName,
            id: additionalColumn.id,
            internalName: toCamelCase(additionalColumn.displayName) || "additionalColumn",
            required: additionalColumn.source.required,
            type: getSchemaPropertyTypeFromOttopilotEventContainerTemplateAdditionalColumnType(additionalColumn.type),
            unique: additionalColumn.source.unique,
        };
    }

    return properties;
}

export function getStorageDatabaseSchemaFromOttopilotEventContainerTemplateAdditionalColumns({
    additionalColumns,
    dispositionColumnConfig,
    organizationalUnitColumnConfig,
}: {
    additionalColumns: Array<OttopilotEventContainerTemplateAdditionalColumn>;
    dispositionColumnConfig: OttopilotDispositionColumnConfig;
    organizationalUnitColumnConfig: OttopilotOrganizationalUnitColumnConfig;
}): StorageDatabaseSchema {
    return {
        properties: getSchemaPropertiesFromOttopilotEventContainerTemplateAdditionalColumns({
            additionalColumns: additionalColumns,
            dispositionColumnConfig: dispositionColumnConfig,
            organizationalUnitColumnConfig: organizationalUnitColumnConfig,
        }),
    };
}
